/**
 * Display helpers for the admin account-sessions list.
 *
 * Two admin surfaces render sessions — the users drawer and the user detail
 * page's Access tab — and they have drifted from each other before. The
 * formatting lives here once so support never sees two different descriptions
 * of the same session.
 *
 * ff-api reconciles this list against Keycloak, so every session shown is live;
 * device, location and last-activity come from the sidecar join and the
 * geolocation/fingerprint lookups behind it.
 */
import type { AdminUserSession } from '@/client/types/admin/users-orgs';

export const formatSessionDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleString() : '—';

/** "4 minutes ago" — how support spots the session still in use. */
export const formatRelative = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['minute', 60],
    ['hour', 3600],
    ['day', 86400],
    ['month', 2592000],
    ['year', 31536000],
  ];
  let chosen = units[0];
  for (const unit of units) if (seconds >= unit[1]) chosen = unit;

  try {
    return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(
      -Math.floor(seconds / chosen[1]),
      chosen[0]
    );
  } catch {
    return null;
  }
};

/** Server-parsed device first; the raw UA is only a fallback. */
export const describeSessionDevice = (session: AdminUserSession): string => {
  const { browser, os, device_model: model } = session;
  if (browser && os) return `${browser} on ${os}`;
  if (browser) return browser;
  if (os) return model ? `${model} · ${os}` : os;
  if (model) return model;
  return session.user_agent ?? 'Unrecognized device';
};

/** "Accra, Greater Accra, Ghana" — resolved from the session IP. */
export const describeSessionLocation = (
  session: AdminUserSession
): string | null => {
  const place = session.city ?? session.locality;

  let countryName = session.country_name ?? null;
  if (session.country) {
    try {
      countryName =
        new Intl.DisplayNames(undefined, { type: 'region' }).of(
          session.country
        ) ??
        countryName ??
        session.country;
    } catch {
      countryName = countryName ?? session.country;
    }
  }

  // Region only when it adds something the city has not already said.
  const parts = [
    place,
    session.region && session.region !== place ? session.region : null,
    countryName,
  ].filter(Boolean) as string[];

  return parts.length ? parts.join(', ') : null;
};

export const describeSessionMeta = (session: AdminUserSession): string[] =>
  [
    formatRelative(session.last_active)
      ? `Active ${formatRelative(session.last_active)}`
      : null,
    session.created_at ? `Started ${formatSessionDate(session.created_at)}` : null,
    session.ip_address ? `IP ${session.ip_address}` : null,
    session.isp,
    session.clients?.length ? `via ${session.clients.join(', ')}` : null,
    session.remember_me ? 'Stays signed in' : null,
  ].filter(Boolean) as string[];

/**
 * Whether to badge this session as unfamiliar.
 *
 * Only an explicit `false` counts: `null` means the device could not be
 * checked, and badging that as "new" would put a warning on most rows and
 * teach support to ignore it.
 */
export const isUnrecognizedDevice = (session: AdminUserSession): boolean =>
  session.is_known_device === false;
